import { motion } from 'motion/react';
import { Heart, Globe, Shield, MessageSquare, Users, GraduationCap, Briefcase, Home as HomeIcon, Sparkles, User } from 'lucide-react';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { useTranslation } from '../i18n';

interface OurStoryPageProps {
  onNavigateHome: () => void;
}

export function OurStoryPage({ onNavigateHome }: OurStoryPageProps) {
  const { t } = useTranslation();

  const challenges = [
    {
      icon: GraduationCap,
      titleKey: 'ourStory.problem.challenges.families.title',
      descriptionKey: 'ourStory.problem.challenges.families.description',
    },
    {
      icon: Briefcase,
      titleKey: 'ourStory.problem.challenges.founders.title',
      descriptionKey: 'ourStory.problem.challenges.founders.description',
    },
    {
      icon: Sparkles,
      titleKey: 'ourStory.problem.challenges.highNetWorth.title',
      descriptionKey: 'ourStory.problem.challenges.highNetWorth.description',
    },
  ];

  const differences = [
    {
      icon: Globe,
      titleKey: 'ourStory.differences.items.bilingual.title',
      descriptionKey: 'ourStory.differences.items.bilingual.description',
    },
    {
      icon: Shield,
      titleKey: 'ourStory.differences.items.execution.title',
      descriptionKey: 'ourStory.differences.items.execution.description',
    },
    {
      icon: MessageSquare,
      titleKey: 'ourStory.differences.items.tailored.title',
      descriptionKey: 'ourStory.differences.items.tailored.description',
    },
    {
      icon: Heart,
      titleKey: 'ourStory.differences.items.relationship.title',
      descriptionKey: 'ourStory.differences.items.relationship.description',
    },
  ];

  const teamMembers = [
    {
      roleKey: 'ourStory.team.members.education.role',
      expertiseKey: 'ourStory.team.members.education.expertise',
    },
    {
      roleKey: 'ourStory.team.members.legal.role',
      expertiseKey: 'ourStory.team.members.legal.expertise',
    },
    {
      roleKey: 'ourStory.team.members.business.role',
      expertiseKey: 'ourStory.team.members.business.expertise',
    },
    {
      roleKey: 'ourStory.team.members.concierge.role',
      expertiseKey: 'ourStory.team.members.concierge.expertise',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Section 1: Hero Banner - Why We Exist */}
      <section className="relative pt-32 pb-24 bg-gradient-to-br from-[#0A1A33] to-[#0A1A33]/90 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-96 h-96 rounded-full bg-[#C6A667] blur-3xl" />
          <div className="absolute bottom-20 left-20 w-96 h-96 rounded-full bg-[#C6A667] blur-3xl" />
        </div>
        
        <div className="relative max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-white mb-6 text-[48px]">
                {t('ourStory.hero.title')}
              </h1>
              <p className="text-white/90 leading-relaxed text-[18px]">
                {t('ourStory.hero.description')}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="rounded-2xl overflow-hidden shadow-2xl"
            >
              <ImageWithFallback
                src="https://images.unsplash.com/photo-1764726337778-0247211c3cc8?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx3YXJtJTIwZmFtaWx5JTIwbGlmZXN0eWxlfGVufDF8fHx8MTc2NTE2MzU5NHww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
                alt="Warm family moment"
                className="w-full h-[400px] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section 2: The Inspiration */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >
            <div className="flex items-start gap-6">
              <div className="flex-shrink-0 mt-1">
                <div className="w-12 h-12 rounded-full bg-[#C6A667]/10 flex items-center justify-center">
                  <Users className="w-6 h-6 text-[#C6A667]" />
                </div>
              </div>
              <div>
                <p className="text-[#0A1A33]/80 leading-relaxed text-[18px]">
                  {t('ourStory.inspiration.team')}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-6">
              <div className="flex-shrink-0 mt-1">
                <div className="w-12 h-12 rounded-full bg-[#C6A667]/10 flex items-center justify-center">
                  <Heart className="w-6 h-6 text-[#C6A667]" />
                </div>
              </div>
              <div>
                <p className="text-[#0A1A33]/80 leading-relaxed text-[18px]">
                  {t('ourStory.inspiration.mission')}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Section 3: The Problem We Solve */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-[#0A1A33] mb-4 text-[36px]">{t('ourStory.problem.title')}</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {challenges.map((challenge, index) => {
              const Icon = challenge.icon;
              return (
                <motion.div
                  key={challenge.titleKey}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                  className="bg-white p-8 rounded-2xl shadow-md hover:shadow-xl transition-shadow duration-300"
                >
                  <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#C6A667]/10 flex items-center justify-center">
                    <Icon className="w-8 h-8 text-[#C6A667]" />
                  </div>
                  <h3 className="text-[#0A1A33] mb-3 text-center">
                    {t(challenge.titleKey)}
                  </h3>
                  <p className="text-[#0A1A33]/70 text-center leading-relaxed">
                    {t(challenge.descriptionKey)}
                  </p>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="text-center"
          >
            <p className="text-[#0A1A33]/60 italic text-[18px]">
              {t('ourStory.problem.tagline')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Section 4: What Makes Us Different */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-[#0A1A33] mb-4 text-[36px]">{t('ourStory.differences.title')}</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {differences.map((difference, index) => {
              const Icon = difference.icon;
              return (
                <motion.div
                  key={difference.titleKey}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="text-center group"
                >
                  <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-br from-[#C6A667]/20 to-[#C6A667]/5 flex items-center justify-center group-hover:from-[#C6A667]/30 group-hover:to-[#C6A667]/10 transition-all duration-300">
                    <Icon className="w-8 h-8 text-[#C6A667]" />
                  </div>
                  <h3 className="text-[#0A1A33] mb-3">
                    {t(difference.titleKey)}
                  </h3>
                  <p className="text-[#0A1A33]/70 leading-relaxed">
                    {t(difference.descriptionKey)}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section 5: Meet the Team */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-[#0A1A33] mb-4 text-[36px]">{t('ourStory.team.title')}</h2>
            <p className="text-[#0A1A33]/60 max-w-2xl mx-auto">
              {t('ourStory.team.subtitle')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {teamMembers.map((member, index) => (
              <motion.div
                key={member.roleKey}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white p-6 rounded-2xl shadow-md hover:shadow-xl transition-shadow duration-300"
              >
                <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-[#0A1A33] to-[#0A1A33]/80 flex items-center justify-center">
                  <User className="w-12 h-12 text-[#C6A667]" />
                </div>
                <h3 className="text-[#0A1A33] mb-3 text-center">
                  {t(member.roleKey)}
                </h3>
                <p className="text-[#0A1A33]/70 text-center leading-relaxed text-[14px]">
                  {t(member.expertiseKey)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 6: Our Promise */}
      <section className="py-32 bg-gradient-to-br from-[#0A1A33] via-[#0D2347] to-[#0A1A33] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-[#C6A667] blur-3xl" />
        </div>
        
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="mb-8">
              <Heart className="w-16 h-16 text-[#C6A667] mx-auto mb-8" />
            </div>
            <blockquote className="text-white text-[32px] leading-relaxed mb-8 italic">
              "{t('ourStory.promise.quote')}"
            </blockquote>
            <div className="w-32 h-1 bg-gradient-to-r from-transparent via-[#C6A667] to-transparent mx-auto" />
          </motion.div>
        </div>
      </section>

      {/* Section 7: Call to Action */}
      <section className="py-24 bg-gradient-to-br from-[#0A1A33] to-[#0D2347]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-white mb-8 text-[32px]">
              {t('ourStory.cta.title')}
            </h2>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              className="px-12 py-5 bg-gradient-to-r from-[#C6A667] to-[#D4B578] hover:from-[#D4B578] hover:to-[#C6A667] text-white rounded-full shadow-xl hover:shadow-2xl transition-all duration-300"
            >
              {t('ourStory.cta.button')}
            </motion.button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
